import React, { Fragment, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import Nav from '../components/Nav.js';
import Button from '../components/Button.js';
import CategoryType from '../components/congress/filterByType/categoryTypes';
import CongressMap from '../components/CongressMap';
import ListView from '../components/congress/navListView/listView';
import { COLORS, IDS_TO_COMMUNITIES, COMMUNITIES_TO_NAMES, RESULTS_PER_PAGE } from '../constants';
import loadingAnimation from '../images/animated-loader.gif';
import iconClearRed from '../images/x-circle-red.png';
import ChevronSVG from '../components/ChevronSVG.js';
import NavFilterByDistrict from '../components/congress/filterByDistrict/NavFilterByDistrict';
import logo from '../images/logo-rebrand.svg';
import Autocomplete from '../components/Autocomplete';
import ModalOverlay from '../components/ModalOverlay';

import { fireTag } from '../libs/analytics.js';

import { sortByName, sortByFeaturedOrder, sortByDistance } from '../libs/sorting';

import FilterStateOpen from '../components/congress/filterByDistrict/FilterStateOpen';
import FilterDistrictOpen from '../components/congress/filterByDistrict/FilterDistrictOpen';
import { GAfirePageView } from '../components/Tracking';
import { shuffle } from 'gsap/gsap-core';


const PlaceholderPage = () => (
	<div className="loadingPage cd">
		<img alt="" src={loadingAnimation}/>
	</div>
);

const SkipToH1 = ({ activePage }) => {
	return (
		<a 
			id="skip-to-main" 
			href={`#${activePage}-main-h1`}
			onClick={(e) => {
				e.preventDefault();
				window.scrollTo({
					top:document.getElementById(`${activePage}-main-h1`).getBoundingClientRect().top - 75, 
					left: 0, 
				});
				document.getElementById(`${activePage}-main-h1`).focus()
			}}
		>Skip to main content.</a>
	)
}

const Congress = ({
					  congress,
					  toggleCongressDropdowns,
					  chooseDropdownItem,
					  chooseDropdownType,
					  uiState,
					  resultsPos,
					  handleEvent,
					  isMobile,
					  orgs,
					  all,
					  textSearchedOrgs,
					  resetMap,
					  loadingFilter,
					  closeNav,
					  onRef,
					  dropDownCongress,
					  checkForCityOrZip,
					  addExitClickListeners,
					  closeExitOverlay,
					  modalOverlay
				  }) => {
	const {searchResultsByCategory, showListView, showFilter} = uiState;
	let {featuredOrgs} = uiState;

	let categoryCount = 0;
	let tracker = 0;
	let searchResults = [];
	const currentPage = resultsPos / RESULTS_PER_PAGE + 1;
	const startPos = resultsPos;
	const endPos = startPos + RESULTS_PER_PAGE;
	let totalFilteredResults = 0;
	let orgsByFeaturedOrder = [];
	let orgsByDistance = [];

	let orgsForCategories = all;
	if (typeof textSearchedOrgs !== 'undefined' && congress.searchType !== '') {
		orgsForCategories = textSearchedOrgs;
	}

	const [randomOrgs, setRandomOrgs] = useState([]);

	useEffect(() => {
		setRandomOrgs(shuffle(orgs));
	}, [orgs]);

	const resultsExpanded = () => !isMobile || (isMobile && showListView);
	const filterExpanded = () => !isMobile || (isMobile && showFilter);
	const mapHeightadjustment = window.innerWidth < 570 ? 207 : 219;

	//////////////////////////////////////////////////////////////////////////////////
	// LEFT RAIL RESULTS - Sorting and filtering logic
	//////////////////////////////////////////////////////////////////////////////////
	if (typeof orgs != 'undefined') {
		if (randomOrgs.length > 0) {
			let isDistanceSearch = false;
			// No more featured orgs
			const featuredOrgsFiltered = randomOrgs.filter(e => e.properties.is_featured === '1'); // filter only for featured orgs
			featuredOrgs = sortByFeaturedOrder(featuredOrgsFiltered); // then sort featured orgs by featured order numbers

			const regularOrgs = randomOrgs.filter(e => e.properties.is_featured === '0'); // filter only for regular orgs
			// const regularOrgs = sortByName(regularOrgsFiltered); // then sort regular orgs by NAME

			
			orgsByFeaturedOrder = featuredOrgs.concat(regularOrgs); // concat the featured org array with regular org array

			if (congress.searchType !== '') { // && congress.searchType !== 'region'
				// a text search was performed

				if (congress.selected.categoryId !== '' && congress.selected.categoryId !== 'all') {
					// filter only for selected category if a category was selected
					// location searches are from mapbox, and category IDs are numerals
					// all other searches (keyword, region, district) are from our DB and category ID is string
					const categoryValueType = congress.searchType === 'postcode' || congress.searchType === 'place'
						? +congress.selected.categoryId
						: congress.selected.categoryId;
					orgsByFeaturedOrder = orgsByFeaturedOrder.filter(e => e.properties.category_id === categoryValueType);

				}

				if (congress.selected.abrvState !== 'US' && congress.searchType === 'keyword') {
					// state was selected in drop down prior to search
					// but only apply when search was a keyword search
					orgsByFeaturedOrder = orgsByFeaturedOrder.filter(e => e.properties.state === congress.selected.abrvState);
					if (congress.selected.district !== 'US') {
						// if district was already selected prior to a text search
						// keep that filter applied as well
						orgsByFeaturedOrder = orgsByFeaturedOrder.filter(e => e.properties.congress_district === congress.selected.abrvState + congress.selected.district);
					}
				}
			}

			if (orgsByFeaturedOrder.length) {
				// makes sure we didn't filter out the orgs to zero by a zero count category or state or district
				if (Object.prototype.hasOwnProperty.call(orgsByFeaturedOrder[0].properties, 'tilequery')) {
					isDistanceSearch = true;
					// sort by distance only if results contain tilequery property
					// when a result has a tilequery property, it has distance from a point, which means it as a location search
					// so we sort by distance from that point
					orgsByDistance = sortByDistance(orgsByFeaturedOrder);
				}

				const {length: orgsToList} = orgsByDistance.length ? orgsByDistance : orgsByFeaturedOrder;

				categoryCount = orgsToList;
				totalFilteredResults += orgsToList;

				if (orgsToList < startPos || orgsToList === 0) {
					tracker += orgsToList;
					return;
				}

				const endSlice = endPos > tracker + orgsToList ? orgsToList : endPos - tracker;
				const startSlice = startPos > tracker ? startPos - tracker : 0;

				if (isDistanceSearch) {
					searchResults.push(...orgsByDistance.slice(startSlice, endSlice));
				}
				else {
					searchResults.push(...orgsByFeaturedOrder.slice(startSlice, endSlice));
				}

				tracker += orgsToList;

			}
			else {
				searchResults = [];
				totalFilteredResults = 0;
				categoryCount = 0;
			}

		}
		else {
			// no orgs were found, so set necessary variables to empty or 0
			// orgsByFeaturedOrder = [];
			searchResults = [];
			totalFilteredResults = 0;
			categoryCount = 0;
		}
	}

	const filterApplied =
		congress.selected.categoryName !== '' ||
		congress.selected.stateName !== 'State' ||
		congress.selected.districtName !== 'District' ||
		congress.searchType !== '';

	GAfirePageView();
	addExitClickListeners();


	const title = "Wells Fargo – Changing how we live tomorrow starts today"
	const ogtitle = "Wells Fargo – Community Impact Map"

	const description = "Explore the Wells Fargo commitment to helping solve challenges & strengthen communities with a focus on housing affordability, financial health & small business growth»"
	const ogdescription = "Explore our community impact map and see how Wells Fargo is making a difference in your community."

	const image = "https://welcome.wf.com/impact/images/default-share.jpg"

	// eslint-disable-next-line consistent-return
	return (
		<>
			<Helmet>
				<title>{`${title}`}</title>
				<link rel="canonical" href="https://welcome.wf.com/impact/map/"/>
				<meta name="robots" content="noindex, nofollow"/>
				<meta property="description" content={`${description}`}/>
				{/* Open Graph Meta Tags */}
				<meta property="og:title" content={`${ogtitle}`}/>
				<meta property="og:url" content="https://www.wellsfargo.com/impact/map/"/>
				<meta property="og:description" content={`${ogdescription}`}/>
				<meta property="og:image" content={`${image}`}/>


				<meta name="twitter:title" content={`${ogtitle}`}/>
				<meta name="twitter:description" content={`${ogdescription}`}/>
				<meta name="twitter:image" content={`${image}`}/>
			</Helmet>
			{isMobile && (
				<Nav
					handleEvent={handleEvent}
					isNotHomePage
					isMobile={isMobile}
					activePage="map"
				/>
			)}
			<div className="IE-for-main district">
				<main data-page="map">
					<section className="organizations results group">
						{!loadingFilter ? '' : <PlaceholderPage/>}
						{/* {orgs !== undefined || zeroResults() ? '' : <PlaceholderPage/>} */}
						{isMobile ? (
							<section className="organizations__search-controls congress-district">
								{/* having the nav links here is an extreme workaround these should be moved to the nav component when time allows */}
								<ul className="nav-links" role="menu">
									<li role="menuitem">
										<Link
											to="/"
											onClick={() => {
												fireTag('G5');
												handleEvent({
													action: 'clear-search',
													type: '',
													name: '',
													origin: 'congress',
												});
											}}
										>Home
										</Link>
									</li>
									<li role="menuitem">
										<Link onClick={()=>fireTag('G6')} to="/map/">Map</Link>
									</li>
								</ul>
								<div className="mobile-district-filters">
									<div className="navOptions row-1">
										<form
											role="search"
											autoComplete="off"
											onSubmit={event => {
												event.preventDefault();
											}}
										>
											<Autocomplete
												orgResults={all}
												currentSearch={uiState.currentSearch}
												checkForCityOrZip={checkForCityOrZip}
												clearAutocomplete={uiState.clearAutocomplete}
												handleTextSearch={
													inputObj => {
														handleEvent({
															action: 'input',
															name: 'results',
															value: inputObj,
															origin: 'congress'
														});
														handleEvent({
															action: 'search',
															type: 'input',
															name: 'results',
															origin: 'congress'
														});
													}
												}
											/>
										</form>
									</div>
									<div className="navOptions row-2">
										<Button
											handleClick={() => {
												handleEvent({
													action: 'toggle',
													type: 'Filter',
													name: 'results',
													origin: 'congress',
												});
											}}
											buttonStyles={classNames('toggle type', {
												open: showFilter,
											})}
											aria-expanded={filterExpanded()}
										>
											{congress.selected.categoryName !== '' && (
												<span className="selected-item-checkmark left">
                                        <div className="icon-checkmark"/>
                                    </span>
											)}
											{congress.selected.categoryName !== ''
												? `${COMMUNITIES_TO_NAMES[congress.selected.categoryName]}`
												: 'Category'}
											<ChevronSVG chevron={showFilter ? 'up' : 'down'} active/>
										</Button>

										{/* category filters UI starts here */}
										<div className="filter-panel" id="filter-cats">
											<button type="button" className="toggle type" id="categoryItem">
												<p>Filter by the cause you care about</p>
												<ChevronSVG/>
											</button>
											{/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
											<div
												className={classNames('menu', {
													open: showFilter,
												})}
												id="filter-menu"
												role="menu"
												data-active-category={congress.selected.categoryName}
												data-categories-filtered="true"
												/* eslint-disable-next-line jsx-a11y/aria-proptypes */
												aria-expanded={filterExpanded()}
											>
												<CategoryType
													chooseDropdownType={chooseDropdownType}
													congress={congress}
													all={orgsForCategories}
													selectedItem={congress.selected.categoryName}
												/>
											</div>
										</div>
										<FilterStateOpen
											chooseDropdownItem={chooseDropdownItem}
											congress={congress}
											toggleCongressDropdowns={toggleCongressDropdowns}
											active
											isMobile={isMobile}
										/>
									</div>
									<div className="navOptions row-3">
										{filterApplied ? (
											<Button
												handleClick={async () => {
													// when resetting the map, we also clear the Mapbox GeoCoder using
													// their .clear() method. This method sets focus to the text input
													// which in turn, on mobile devices, keeps the soft keyboard open so
													// we set focus to the magnifying glass buttons after the reset
													// logic is complete
													await resetMap().then(() => {
														document.getElementById('search-btn').focus();
													})
												}}
												buttonStyles="mobile-clear-search"
												altText="Clear Search"
												type="submit"
											>
												Clear Filters
												<img src={iconClearRed} className="icon-x results" alt="search icon"/>
											</Button>
										) : (
											<div className="spacer"/>
										)}
										<FilterDistrictOpen
											chooseDropdownItem={chooseDropdownItem}
											congress={congress}
											toggleCongressDropdowns={toggleCongressDropdowns}
											isMobile={isMobile}
											districtInfo={congress.districts[congress.selected.district]}
										/>
									</div>
								</div>
							</section>
						) : (
							<nav className="organizations__search-controls congress-district">
								{/* having the logo and nav links here is an extreme workaround
                            these should be moved to the nav component when time allows */}
								<SkipToH1 activePage="map" />
								<Link
									className="logo"
									to="/"
									onClick={() => {
										fireTag('G1');
										handleEvent({
											action: 'clear-search',
											type: '',
											name: '',
											origin: 'congress',
										});
									}}
								>
									<img src={logo} alt="Wells Fargo logo"/>
								</Link>
								<div className="district-top-nav">
									<ul className="nav-links" role="menu">
										<li role="menuitem">
											<Link
												to="/"
												onClick={() => {
													fireTag('G5');
													handleEvent({
														action: 'clear-search',
														type: '',
														name: '',
														origin: 'congress',
													});
												}}
											>Home
											</Link>
										</li>
										<li role="menuitem">
											<Link to="/map/" onClick={()=>fireTag('G6')}>Map</Link>
										</li>
									</ul>
									<form
										role="search"
										autoComplete="off"
										onSubmit={event => {
											event.preventDefault();
										}}
									>
										<Autocomplete
											orgResults={all}
											currentSearch={uiState.currentSearch}
											checkForCityOrZip={checkForCityOrZip}
											clearAutocomplete={uiState.clearAutocomplete}
											handleTextSearch={
												inputObj => {
													handleEvent({
														action: 'input',
														name: 'results',
														value: inputObj,
														origin: 'congress'
													});
													handleEvent({
														action: 'search',
														type: 'input',
														name: 'results',
														origin: 'congress'
													});
												}
											}
										/>
									</form>
								</div>
								<div className="congress-filter-congress-district">
									<NavFilterByDistrict
										congress={congress}
										chooseDropdownItem={chooseDropdownItem}
										filterDistrict={congress.filterDistrict}
										toggleCongressDropdowns={toggleCongressDropdowns}
										isMobile={isMobile}
										ref={dropDownCongress}
									/>
								</div>
							</nav>
						)}
						<div className="organizations__map congress-district" style={isMobile ? {height: `${window.innerHeight - mapHeightadjustment}px`} : {}}>
							{/* left rail search results listing starts here */}

							<div
								className={classNames('results-list cd', {
									'close-list': closeNav,
								})}
							>
								{/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
								<section
									aria-expanded={resultsExpanded()}
									className={classNames('organizations__results-list menu cd-exp', {
										open: uiState.showListView,
									})}
								>
									{typeof searchResults != 'undefined' && (
										<ListView
											searchResults={searchResults}
											uiState={uiState}
											handleEvent={handleEvent}
											categoryCount={categoryCount}
											categoryName={congress.selected.categoryName}
											USstateName={congress.selected.stateName}
											districtName={congress.selected.districtName}
											currentPage={currentPage}
											totalFilteredResults={totalFilteredResults}
											searchResultsByCategory={searchResultsByCategory}
											resetMap={resetMap}
											isMobile={isMobile}
											resultsExpanded={resultsExpanded()}
											searchType={congress.searchType}
										/>
									)}

								</section>
								{!isMobile && (
									<Button
										handleClick={() => {
											handleEvent({
												action: 'slide-list-view',
											});
										}}
										buttonStyles="collapse-list-view"
										aria-label="Shows and hides the organizations results list."
										/* eslint-disable-next-line jsx-a11y/aria-proptypes */
										aria-expanded={`${closeNav}`}
									>
										<div className="chevron">
											<span className={closeNav ? 'right' : 'left'}/>
										</div>
									</Button>
								)}
							</div>
							{/* DESKTOP category filters UI starts here */}
							{!isMobile && (
								<div className="filter-panel" id="filter-cats">
									<button type="button" className="toggle type" id="categoryItem">
										<p>Filter by the cause you care about</p>
										<ChevronSVG/>
									</button>
									{/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
									<div
										className={classNames('menu', {
											open: showFilter,
										})}
										id="filter-menu"
										role="menu"
										data-active-category={congress.selected.categoryName}
										data-categories-filtered="true"
										/* eslint-disable-next-line jsx-a11y/aria-proptypes */
										aria-expanded="true"
									>
										<CategoryType
											chooseDropdownType={chooseDropdownType}
											congress={congress}
											all={orgsForCategories}
										/>
									</div>
								</div>
							)}
							<CongressMap
								onRef={onRef}
								congress={congress}
								searchResults={searchResults}
								searchResultsByCategory={searchResultsByCategory}
								currentPage={currentPage}
								loadingFilter={loadingFilter}
								closeNav={closeNav}
								uiState={uiState}
							/>
						</div>
					</section>
				</main>
			</div>
			{modalOverlay && modalOverlay.open && (
				<ModalOverlay closeExitOverlay={closeExitOverlay} modalOverlay={modalOverlay}/>
			)}
		</>
	);
};

export default withRouter(Congress);
